import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Paper,
    Skeleton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { Add, Campaign as CampaignIcon, LocalOffer, OpenInNew, StopCircleOutlined } from "@mui/icons-material";
import { CampaignStatus, CampaignSummary, campaignService } from "../../services/campaignService";
import { campaignPageUrl } from "../../config/storefront";
import { formatCurrencyBDT } from "../../utils/currency";

export const STATUS_CHIP: Record<CampaignStatus, { label: string; color: "success" | "info" | "default" }> = {
    live: { label: "Live", color: "success" },
    scheduled: { label: "Scheduled", color: "info" },
    ended: { label: "Ended", color: "default" },
};

const DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", timeZone: "Asia/Dhaka" });
const DATE_TIME = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    timeZone: "Asia/Dhaka",
});

/** "In 3 days" / "in 5 hours" / "in 20 min" — for a moment still to come. */
export function fromNow(iso: string): string {
    const ms = new Date(iso).getTime() - Date.now();
    const min = Math.max(1, Math.round(ms / 60000));
    if (min < 60) return `in ${min} min`;
    const hrs = Math.round(min / 60);
    if (hrs < 48) return `in ${hrs} hour${hrs === 1 ? "" : "s"}`;
    const days = Math.round(hrs / 24);
    return `in ${days} days`;
}

export function whenText(c: Pick<CampaignSummary, "status" | "starts_at" | "ends_at">): string {
    if (c.status === "scheduled") return `Starts ${fromNow(c.starts_at)} · ${DATE_TIME.format(new Date(c.starts_at))}`;
    if (c.status === "live") return c.ends_at ? `Ends ${fromNow(c.ends_at)} · ${DATE_TIME.format(new Date(c.ends_at))}` : "Runs until you end it";
    return `${DATE.format(new Date(c.starts_at))} – ${DATE.format(new Date(c.ends_at ?? c.starts_at))}`;
}

/** How products added to it are priced (each price stays editable). */
export function discountText(c: Pick<CampaignSummary, "discount_type" | "discount_value">): string {
    if (c.discount_type === "percent") return `${Number(c.discount_value)}% off by default`;
    if (c.discount_type === "flat") return `${formatCurrencyBDT(Number(c.discount_value), { ignoreDecimals: true })} off by default`;
    return "Prices set per product";
}

type Filter = "all" | CampaignStatus;

/**
 * Campaigns — promotions with their own page on the website, a homepage
 * section and a badge on their products, optionally with a discount.
 */
export const CampaignList: React.FC = () => {
    const navigate = useNavigate();
    const [campaigns, setCampaigns] = useState<CampaignSummary[] | null>(null);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState<Filter>("all");
    const [ending, setEnding] = useState<CampaignSummary | null>(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        setError("");
        try {
            setCampaigns(await campaignService.list());
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't load campaigns.");
            setCampaigns([]);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const counts = useMemo(() => {
        const c = { all: 0, live: 0, scheduled: 0, ended: 0 } as Record<Filter, number>;
        for (const x of campaigns ?? []) {
            c.all += 1;
            c[x.status] += 1;
        }
        return c;
    }, [campaigns]);

    // Live first, then upcoming, then past.
    const shown = useMemo(() => {
        const order: Record<CampaignStatus, number> = { live: 0, scheduled: 1, ended: 2 };
        return (campaigns ?? [])
            .filter((c) => filter === "all" || c.status === filter)
            .sort((a, b) => order[a.status] - order[b.status] || b.starts_at.localeCompare(a.starts_at));
    }, [campaigns, filter]);

    const endNow = async () => {
        if (!ending) return;
        setBusy(true);
        try {
            await campaignService.endNow(ending.campaign_id);
            setEnding(null);
            await load();
        } catch (err: any) {
            setError(err?.response?.data?.detail || "Couldn't end the campaign.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
                <Box sx={{ minWidth: 0, maxWidth: 720 }}>
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        sx={{ fontSize: { xs: "1.6rem", sm: "2.125rem" }, display: "flex", alignItems: "center", gap: 1 }}
                    >
                        <CampaignIcon color="primary" /> Campaigns
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Promotions like "Eid Offer" or "11.11": a page on the website, a homepage section and a badge
                        on their products — with campaign prices that start and stop by themselves.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate("/campaigns/new")}>
                    New campaign
                </Button>
            </Box>

            <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: "wrap", rowGap: 1 }}>
                {(["all", "live", "scheduled", "ended"] as Filter[]).map((f) => (
                    <Chip
                        key={f}
                        label={`${f === "all" ? "All" : STATUS_CHIP[f].label} (${counts[f]})`}
                        color={filter === f ? "primary" : "default"}
                        variant={filter === f ? "filled" : "outlined"}
                        onClick={() => setFilter(f)}
                    />
                ))}
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
                    {error}
                </Alert>
            )}

            {campaigns === null ? (
                <Stack spacing={1.5}>
                    {[0, 1, 2].map((i) => (
                        <Skeleton key={i} variant="rounded" height={112} />
                    ))}
                </Stack>
            ) : shown.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: "center", borderRadius: 2, borderStyle: "dashed" }}>
                    <LocalOffer color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                    <Typography fontWeight={600}>
                        {campaigns.length === 0 ? "No campaigns yet" : "Nothing here"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {campaigns.length === 0
                            ? "Create one, add its products, and it goes live on the date you choose."
                            : "No campaigns with this status."}
                    </Typography>
                    {campaigns.length === 0 && (
                        <Button variant="outlined" startIcon={<Add />} onClick={() => navigate("/campaigns/new")}>
                            New campaign
                        </Button>
                    )}
                </Paper>
            ) : (
                <Stack spacing={1.5}>
                    {shown.map((c) => (
                        <Paper
                            key={c.campaign_id}
                            variant="outlined"
                            onClick={() => navigate(`/campaigns/${c.campaign_id}`)}
                            sx={{
                                borderRadius: 2,
                                overflow: "hidden",
                                cursor: "pointer",
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                opacity: c.status === "ended" ? 0.75 : 1,
                                "&:hover": { borderColor: "primary.main" },
                            }}
                        >
                            {/* Banner thumbnail */}
                            <Box
                                sx={{
                                    width: { xs: "100%", sm: 220 },
                                    aspectRatio: { xs: "4 / 1", sm: "auto" },
                                    minHeight: { sm: 100 },
                                    flexShrink: 0,
                                    bgcolor: c.banner_image_url ? "grey.100" : "#b4471c",
                                    backgroundImage: c.banner_image_url
                                        ? `url("${c.banner_image_url}")`
                                        : "linear-gradient(135deg, #b4471c, #e8833f)",
                                    backgroundSize: "cover",
                                    backgroundPosition: "center",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "#fff",
                                    filter: c.status === "ended" ? "grayscale(70%)" : undefined,
                                }}
                            >
                                {!c.banner_image_url && <LocalOffer />}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, p: 1.75, display: "flex", flexDirection: "column", gap: 0.75 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                                    <Typography fontWeight={700} sx={{ minWidth: 0 }} noWrap>
                                        {c.name}
                                    </Typography>
                                    <Chip size="small" label={STATUS_CHIP[c.status].label} color={STATUS_CHIP[c.status].color} />
                                    {c.show_on_home && c.status !== "ended" && (
                                        <Chip size="small" variant="outlined" label="On homepage" />
                                    )}
                                </Box>
                                <Typography variant="body2" color="text.secondary">
                                    {whenText(c)}
                                </Typography>
                                <Typography variant="body2">
                                    {c.product_count} product{c.product_count === 1 ? "" : "s"} · {discountText(c)}
                                    {c.max_discount_percent ? ` · up to ${c.max_discount_percent}% off` : ""}
                                </Typography>
                            </Box>
                            <Box
                                sx={{ display: "flex", alignItems: "center", gap: 0.5, px: 1, pb: { xs: 1, sm: 0 } }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Tooltip title="View on website">
                                    <IconButton
                                        component="a"
                                        href={campaignPageUrl(c.slug)}
                                        target="_blank"
                                        rel="noopener"
                                        aria-label="View on website"
                                    >
                                        <OpenInNew fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {c.status !== "ended" && (
                                    <Tooltip title="End now">
                                        <IconButton onClick={() => setEnding(c)} aria-label="End now">
                                            <StopCircleOutlined fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            )}

            <Dialog open={!!ending} onClose={() => !busy && setEnding(null)}>
                <DialogTitle>End “{ending?.name}” now?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Its products go back to their regular prices straight away, its badge and homepage section
                        disappear, and its page says the offer has ended. To run it again later, open it and set
                        new dates.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEnding(null)} disabled={busy}>
                        Keep running
                    </Button>
                    <Button color="error" variant="contained" onClick={endNow} disabled={busy}>
                        End now
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CampaignList;
